import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, throwError } from 'rxjs';
import { tap, map, catchError, switchMap, timeout, finalize, shareReplay } from 'rxjs/operators';
import { StorageService, AuthService } from '../services';
import { SHARED_ENV, AUTH_ROUTES } from '@trading-fe/shared';
import {
  NetworkErrorType,
  classifyHttpError,
  networkErrorMessage,
  networkErrorTypeKey,
} from '../helpers/network-error-type';

export { NetworkErrorType } from '../helpers/network-error-type';

/**
 * GTN request timeout in milliseconds.
 * Mobile reference: `ConnectionConfig.connectTimeout = Duration(seconds: 60)`
 */
const GTN_TIMEOUT_MS = 60_000;

export class FeError {
  public message: string;
  public type: string;
  public data: any;
  public networkErrorType: NetworkErrorType;

  constructor(
    message: string,
    type: string,
    data?: any,
    networkErrorType: NetworkErrorType = NetworkErrorType.Unknown,
  ) {
    this.message = message;
    this.data = data;
    this.type = type;
    this.networkErrorType = networkErrorType;
  }

  public toString = (): string => {
    return this.message;
  };
}

let isHandlingUnauthorized = false;
// A page that fires several requests the role may not make would otherwise stack one toast per call.
let lastPermissionDeniedAt = 0;

/**
 * Shared in-flight GTN token refresh observable.
 * Mobile equivalent: `TokenManager` singleton with `isRefreshing` / `Completer`.
 *
 * When multiple GTN requests fail with 401 concurrently, only one refresh
 * is executed; the others wait for its result via `shareReplay(1)`.
 */
let gtnRefreshInFlight$: Observable<any> | null = null;

export const tokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const env = inject(SHARED_ENV);
  const storage = inject(StorageService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const routes = inject(AUTH_ROUTES);
  const message = inject(NzMessageService);

  const isGtnRequest = !!(env.gtnHost && req.url.startsWith(env.gtnHost));

  if (!req.url.includes('/api/') && !isGtnRequest) {
    return next(req);
  }

  let lang = localStorage.getItem('lang');
  if (lang == null) {
    lang = 'en';
  }

  req = req.clone({
    setHeaders: {
      'Accept-Language': lang,
    },
  });

  // auth header
  if (!req.headers.has('Authorization')) {
    if (isGtnRequest) {
      const gtnToken = storage.getItem('gtnToken');
      if (gtnToken) {
        req = req.clone({
          setHeaders: {
            Authorization: `Bearer ${gtnToken}`,
          },
        });
      }
    } else {
      const token = storage.getItem('token');
      if (token) {
        req = req.clone({
          setHeaders: {
            Authorization: token,
          },
        });
      }
    }
  }

  const started = Date.now();

  const unauthorizedHandler = () => {
    if (isHandlingUnauthorized) return;
    isHandlingUnauthorized = true;
    authService.clearStorage();
    router.navigateByUrl(routes.loginUrl, { replaceUrl: true }).then(() => {
      isHandlingUnauthorized = false;
    });
  };

  const constructErrorMessage = (responseBody: any): string => {
    return responseBody.message;
  };

  /**
   * Extract error message from a response body that may use either the flat
   * `{ type, message }` format or the nested `{ error: { type, message } }`.
   *
   * Mobile reference: `NetworkingUtils.extractErrorMessage()`
   */
  const extractErrorMessage = (body: any): string => {
    try {
      if (body?.type != null) {
        return body.message ?? 'Unknown error occurred. Please try again.';
      }
      if (body?.error?.type != null) {
        return body.error.message ?? 'Unknown error occurred. Please try again.';
      }
    } catch {
      // Malformed body
    }
    return 'Unknown error occurred. Please try again.';
  };

  /**
   * Extract error type string from a response body.
   *
   * Mobile reference: `NetworkingUtils.getErrorType()`
   */
  const extractErrorType = (body: any): string => {
    try {
      if (body?.type != null) return body.type;
      if (body?.error?.type != null) return body.error.type;
    } catch {
      // Malformed body
    }
    return '';
  };

  /**
   * Force logout — clear all state and redirect to login.
   * Mobile reference: `GtnAppDioInterceptor._forceLogout()`
   */
  const forceLogout = (reason: string) => {
    console.error(`[tokenInterceptor] Forcing logout: ${reason}`);
    unauthorizedHandler();
  };

  /**
   * Two-step GTN token refresh matching mobile's flow:
   *   1. Try GTN native refresh (POST /trade/auth/customer/token/refresh)
   *   2. If that fails, recover from backend (GET /auth/gtn/token)
   *
   * Concurrent coordination: only one refresh runs at a time.
   * Mobile reference: `GtnAppDioInterceptor._refreshTokenOnce()` + `_recoverGtnTokenFromBackend()`
   */
  const refreshGtnToken = (): Observable<any> => {
    if (gtnRefreshInFlight$) {
      return gtnRefreshInFlight$;
    }

    gtnRefreshInFlight$ = authService.refreshGtnTokenViaGtn().pipe(
      catchError((refreshErr) => {
        console.error('[tokenInterceptor] GTN native refresh failed, attempting backend recovery:', refreshErr);
        return authService.fetchGtnToken();
      }),
      finalize(() => {
        gtnRefreshInFlight$ = null;
      }),
      shareReplay(1),
    );

    return gtnRefreshInFlight$;
  };

  // Build the response pipeline
  let response$ = next(req);

  // Apply timeout to GTN requests (mobile: 60s connect + 60s receive)
  if (isGtnRequest) {
    response$ = response$.pipe(
      timeout(GTN_TIMEOUT_MS),
    );
  }

  return response$.pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const elapsed = Date.now() - started;
        // logger.debug(`Request for ${req.urlWithParams} took ${elapsed} ms.`);
      }
    }),
    map((event) => {
      if (event instanceof HttpResponse) {
        // GTN responses have a different format — pass through without unwrapping
        if (isGtnRequest) {
          return event;
        }

        const copyEevent = event.clone() as any;
        if (!copyEevent.body.status) {
          if (copyEevent.body.type === 'ERROR_UNAUTHORIZED') {
            unauthorizedHandler();
          }

          if (copyEevent.body.code === 503) {
            if (routes.serviceUnavailableUrl) {
              sessionStorage.setItem('serviceUnavailable', 'true');
              router.navigateByUrl(routes.serviceUnavailableUrl, { replaceUrl: true });
            } else {
              authService.clearStorage();
              router.navigateByUrl(routes.loginUrl, { replaceUrl: true });
            }
          }

          if (copyEevent.body.code === 801) {
            router.navigateByUrl(routes.tfaUrl!, { replaceUrl: true });
          }

          const messageText = constructErrorMessage(event.body);

          // The session is fine, the role just may not do this: tell the user, keep them logged in.
          if (copyEevent.body.type === 'PERMISSION_DENIED') {
            const now = Date.now();
            if (now - lastPermissionDeniedAt > 2000) {
              lastPermissionDeniedAt = now;
              message.error(messageText || 'Permission denied');
            }
          }

          throw new FeError(
            messageText,
            copyEevent.body.type,
            copyEevent.body.data
          );
        } else {
          return event.clone({ body: copyEevent.body.result });
        }
      }

      return event;
    }),
    catchError((err: any) => {
      // ---- RxJS timeout produces a non-HttpErrorResponse error ----
      // Convert it into an HttpErrorResponse-like path so the classification below works.
      if (isGtnRequest && err?.name === 'TimeoutError') {
        return throwError(() => new FeError(
          networkErrorMessage(NetworkErrorType.Timeout),
          networkErrorTypeKey(NetworkErrorType.Timeout),
          undefined,
          NetworkErrorType.Timeout,
        ));
      }

      // ---- GTN 401: two-step refresh then retry (matching mobile) ----
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        isGtnRequest &&
        !req.headers.has('X-Gtn-Retry')
      ) {
        // Mobile: skip refresh for the /refresh endpoint itself
        if (req.url.includes('/trade/auth/customer/token/refresh')) {
          // Go straight to backend recovery
          return authService.fetchGtnToken().pipe(
            switchMap(() => {
              const newGtnToken = storage.getItem('gtnToken');
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newGtnToken}`,
                  'X-Gtn-Retry': '1',
                },
              });
              return next(retryReq);
            }),
            catchError(() => {
              forceLogout('GTN token recovery from backend failed after /refresh 401');
              return throwError(() => err);
            }),
          );
        }

        return refreshGtnToken().pipe(
          switchMap(() => {
            const newGtnToken = storage.getItem('gtnToken');
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newGtnToken}`,
                'X-Gtn-Retry': '1',
              },
            });
            return next(retryReq).pipe(
              timeout(GTN_TIMEOUT_MS),
            );
          }),
          catchError((retryErr) => {
            // Both refresh steps failed — force logout
            if (retryErr instanceof HttpErrorResponse && retryErr.status === 401) {
              forceLogout('GTN request retry failed with 401 after token refresh');
            }
            // Classify the retry error for the caller
            if (retryErr instanceof HttpErrorResponse) {
              const errorType = classifyHttpError(retryErr);
              return throwError(() => new FeError(
                networkErrorMessage(errorType),
                networkErrorTypeKey(errorType),
                retryErr.error?.data,
                errorType,
              ));
            }
            return throwError(() => err);
          }),
        );
      }

      // ---- HttpErrorResponse classification ----
      if (err instanceof HttpErrorResponse) {
        if (isGtnRequest) {
          // GTN HTTP errors: classify and wrap with NetworkErrorType
          const errorType = classifyHttpError(err);

          // Navigation for specific network error types
          if (errorType === NetworkErrorType.NoInternet && routes.networkErrorUrl) {
            router.navigateByUrl(routes.networkErrorUrl, { replaceUrl: true });
          }
          if (errorType === NetworkErrorType.Maintenance && routes.serviceUnavailableUrl) {
            sessionStorage.setItem('serviceUnavailable', 'true');
            router.navigateByUrl(routes.serviceUnavailableUrl, { replaceUrl: true });
          }

          // Extract message from GTN error body if present
          const body = err.error;
          const gtnMessage = body ? extractErrorMessage(body) : networkErrorMessage(errorType);
          const gtnType = body ? (extractErrorType(body) || networkErrorTypeKey(errorType)) : networkErrorTypeKey(errorType);

          return throwError(() => new FeError(
            gtnMessage,
            gtnType,
            body?.data,
            errorType,
          ));
        }

        // ---- Non-GTN (backend-proxied) HTTP errors ----

        // Network error — browser couldn't reach server
        if (err.status === 0 && routes.networkErrorUrl) {
          router.navigateByUrl(routes.networkErrorUrl, { replaceUrl: true });
          return throwError(() => new FeError(
            networkErrorMessage(NetworkErrorType.NoInternet),
            networkErrorTypeKey(NetworkErrorType.NoInternet),
            undefined,
            NetworkErrorType.NoInternet,
          ));
        }

        // HTTP-level 503 Service Unavailable
        if (err.status === 503) {
          if (routes.serviceUnavailableUrl) {
            sessionStorage.setItem('serviceUnavailable', 'true');
            router.navigateByUrl(routes.serviceUnavailableUrl, { replaceUrl: true });
          }
          return throwError(() => new FeError(
            networkErrorMessage(NetworkErrorType.Maintenance),
            networkErrorTypeKey(NetworkErrorType.Maintenance),
            undefined,
            NetworkErrorType.Maintenance,
          ));
        }

        // HTTP-level 504 Gateway Timeout
        if (err.status === 504) {
          return throwError(() => new FeError(
            networkErrorMessage(NetworkErrorType.Timeout),
            networkErrorTypeKey(NetworkErrorType.Timeout),
            undefined,
            NetworkErrorType.Timeout,
          ));
        }

        const body = err.error;
        if (body && body.type) {
          return throwError(() => new FeError(
            body.message || 'Network Error',
            body.type,
            body.data,
            classifyHttpError(err),
          ));
        }

        // Fallback: classify the error even without a typed body
        const fallbackType = classifyHttpError(err);
        return throwError(() => new FeError(
          networkErrorMessage(fallbackType),
          networkErrorTypeKey(fallbackType),
          undefined,
          fallbackType,
        ));
      }

      // ---- Non-HTTP errors (FeError from the map() above, etc.) ----
      return throwError(() => err);
    })
  );
};
